import type { ComicBubbleObject, ComicBubbleType, ComicPanel, Point } from "./internalTypes";
import { getBubbleRotatedVisualBounds } from "./BubbleVisualGeometry";

export const MIN_BUBBLE_WIDTH = 96;
export const MIN_BUBBLE_HEIGHT = 64;

export type BubbleHitRegion = "body" | "resize" | "rotate";

function rotatePoint(point: Point, centre: Point, radians: number): Point {
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  return {
    x: centre.x + dx * cosine - dy * sine,
    y: centre.y + dx * sine + dy * cosine,
  };
}

export function bubbleCentre(object: ComicBubbleObject): Point {
  return { x: object.x + object.width / 2, y: object.y + object.height / 2 };
}

export function normalizeRotation(rotation: number): number {
  if (!Number.isFinite(rotation)) return 0;
  let normalized = rotation % 360;
  if (normalized > 180) normalized -= 360;
  if (normalized < -180) normalized += 360;
  return normalized;
}

export function clampBubbleToPanel(
  object: ComicBubbleObject,
  panel: ComicPanel,
): ComicBubbleObject {
  const rotation = normalizeRotation(object.rotation);
  const panelX = Number.isFinite(panel.x) ? panel.x : 0;
  const panelY = Number.isFinite(panel.y) ? panel.y : 0;
  const maxWidth = Number.isFinite(panel.width) ? Math.max(1, panel.width) : 1;
  const maxHeight = Number.isFinite(panel.height) ? Math.max(1, panel.height) : 1;
  const sourceWidth = Number.isFinite(object.width) && object.width > 0 ? object.width : 1;
  const sourceHeight = Number.isFinite(object.height) && object.height > 0 ? object.height : 1;
  let width = Math.min(maxWidth, Math.max(Math.min(MIN_BUBBLE_WIDTH, maxWidth), sourceWidth));
  let height = Math.min(maxHeight, Math.max(Math.min(MIN_BUBBLE_HEIGHT, maxHeight), sourceHeight));
  let strokeWidth =
    Number.isFinite(object.data.strokeWidth) && object.data.strokeWidth > 0
      ? object.data.strokeWidth
      : 0;
  const requestedCentre = {
    x: Number.isFinite(object.x) ? object.x + sourceWidth / 2 : panelX + maxWidth / 2,
    y: Number.isFinite(object.y) ? object.y + sourceHeight / 2 : panelY + maxHeight / 2,
  };

  let candidate: ComicBubbleObject = {
    ...object,
    x: requestedCentre.x - width / 2,
    y: requestedCentre.y - height / 2,
    width,
    height,
    rotation,
    data: { ...object.data, strokeWidth },
  };
  const initialBounds = getBubbleRotatedVisualBounds(candidate);
  const fitScale = Math.min(
    1,
    maxWidth / Math.max(Number.EPSILON, initialBounds.width),
    maxHeight / Math.max(Number.EPSILON, initialBounds.height),
  );
  if (fitScale < 1) {
    width *= fitScale;
    height *= fitScale;
    strokeWidth *= fitScale;
    candidate = {
      ...candidate,
      x: requestedCentre.x - width / 2,
      y: requestedCentre.y - height / 2,
      width,
      height,
      data: { ...candidate.data, strokeWidth },
    };
  }

  const bounds = getBubbleRotatedVisualBounds(candidate);
  const centre = bubbleCentre(candidate);
  const leftOffset = bounds.x - centre.x;
  const rightOffset = bounds.x + bounds.width - centre.x;
  const topOffset = bounds.y - centre.y;
  const bottomOffset = bounds.y + bounds.height - centre.y;
  const minimumCentreX = panelX - leftOffset;
  const maximumCentreX = panelX + maxWidth - rightOffset;
  const minimumCentreY = panelY - topOffset;
  const maximumCentreY = panelY + maxHeight - bottomOffset;
  const clampCentre = (minimum: number, maximum: number, requested: number, fallback: number) => {
    if (minimum <= maximum) return Math.min(maximum, Math.max(minimum, requested));
    if (minimum - maximum <= 0.000_001) return (minimum + maximum) / 2;
    return fallback;
  };
  const centreX = clampCentre(
    minimumCentreX,
    maximumCentreX,
    requestedCentre.x,
    panelX + maxWidth / 2,
  );
  const centreY = clampCentre(
    minimumCentreY,
    maximumCentreY,
    requestedCentre.y,
    panelY + maxHeight / 2,
  );
  return {
    ...candidate,
    x: centreX - width / 2,
    y: centreY - height / 2,
  };
}

export function createDefaultBubble(
  type: ComicBubbleType,
  panel: ComicPanel,
  point: Point,
  layerId: string,
  order: number,
  id: string = crypto.randomUUID(),
): ComicBubbleObject {
  const narration = type === "narrationBox";
  const width = Math.min(panel.width * 0.62, Math.max(180, panel.width * 0.34));
  const height = narration
    ? Math.min(panel.height * 0.28, Math.max(100, panel.height * 0.16))
    : Math.min(panel.height * 0.42, Math.max(140, panel.height * 0.24));
  return clampBubbleToPanel(
    {
      id,
      type,
      panelId: panel.id,
      x: point.x - width / 2,
      y: point.y - height / 2,
      width,
      height,
      rotation: 0,
      layerId,
      order,
      data: {
        text: "Type here",
        fontSize: Math.max(18, Math.min(42, Math.round(Math.min(width, height) * 0.2))),
        textAlign: narration ? "left" : "center",
        fillColor: narration ? "#fff5d6" : "#ffffff",
        strokeColor: "#202235",
        strokeWidth: Math.max(3, Math.min(10, panel.width * 0.006)),
        tailDirection: narration ? undefined : "bottom-right",
      },
    },
    panel,
  );
}

export function hitTestBubble(
  object: ComicBubbleObject,
  point: Point,
  handleRadius: number,
): BubbleHitRegion | null {
  const centre = bubbleCentre(object);
  const local = rotatePoint(point, centre, (-object.rotation * Math.PI) / 180);
  const resize = { x: object.x + object.width, y: object.y + object.height };
  const rotate = { x: centre.x, y: object.y - handleRadius * 1.8 };
  if (Math.hypot(local.x - resize.x, local.y - resize.y) <= handleRadius) return "resize";
  if (Math.hypot(local.x - rotate.x, local.y - rotate.y) <= handleRadius) return "rotate";
  if (
    local.x >= object.x &&
    local.x <= object.x + object.width &&
    local.y >= object.y &&
    local.y <= object.y + object.height
  ) {
    return "body";
  }
  return null;
}

export function moveBubble(
  original: ComicBubbleObject,
  delta: Point,
  panel: ComicPanel,
): ComicBubbleObject {
  return clampBubbleToPanel(
    { ...original, x: original.x + delta.x, y: original.y + delta.y },
    panel,
  );
}

export function resizeBubble(
  original: ComicBubbleObject,
  documentPoint: Point,
  panel: ComicPanel,
): ComicBubbleObject {
  const centre = bubbleCentre(original);
  const local = rotatePoint(documentPoint, centre, (-original.rotation * Math.PI) / 180);
  return clampBubbleToPanel(
    {
      ...original,
      width: Math.max(MIN_BUBBLE_WIDTH, local.x - original.x),
      height: Math.max(MIN_BUBBLE_HEIGHT, local.y - original.y),
    },
    panel,
  );
}

export function getBubbleResizeHandle(object: ComicBubbleObject): Point {
  const centre = bubbleCentre(object);
  return rotatePoint(
    { x: object.x + object.width, y: object.y + object.height },
    centre,
    (object.rotation * Math.PI) / 180,
  );
}

export function rotateBubble(
  original: ComicBubbleObject,
  documentPoint: Point,
  panel: ComicPanel,
): ComicBubbleObject {
  const centre = bubbleCentre(original);
  const angle =
    (Math.atan2(documentPoint.y - centre.y, documentPoint.x - centre.x) * 180) / Math.PI;
  return clampBubbleToPanel({ ...original, rotation: normalizeRotation(angle + 90) }, panel);
}

export function rotateBubbleByPointerDelta(
  original: ComicBubbleObject,
  startPoint: Point,
  currentPoint: Point,
  panel: ComicPanel,
): ComicBubbleObject {
  const centre = bubbleCentre(original);
  const startAngle = Math.atan2(startPoint.y - centre.y, startPoint.x - centre.x);
  const currentAngle = Math.atan2(currentPoint.y - centre.y, currentPoint.x - centre.x);
  const delta = ((currentAngle - startAngle) * 180) / Math.PI;
  return clampBubbleToPanel(
    { ...original, rotation: normalizeRotation(original.rotation + delta) },
    panel,
  );
}

export function duplicateBubbleToPanel(
  object: ComicBubbleObject,
  source: ComicPanel,
  target: ComicPanel,
  id: string,
  order: number,
): ComicBubbleObject {
  const scaleX = target.width / Math.max(1, source.width);
  const scaleY = target.height / Math.max(1, source.height);
  const styleScale = Math.min(scaleX, scaleY);
  return clampBubbleToPanel(
    {
      ...object,
      id,
      panelId: target.id,
      x: target.x + (object.x - source.x) * scaleX,
      y: target.y + (object.y - source.y) * scaleY,
      width: object.width * scaleX,
      height: object.height * scaleY,
      order,
      data: {
        ...object.data,
        fontSize: Math.max(12, object.data.fontSize * styleScale),
        strokeWidth: object.data.strokeWidth * styleScale,
      },
    },
    target,
  );
}
